import { useEffect } from 'react'
import { useView } from '../../contexts/ViewContext'
import { useData } from '../../contexts/DataContext'
import { useFilters } from '../../contexts/FilterContext'
import {
  getAvailableServices,
  getAvailableInfrastructure,
} from '../../utils/facilityUtils'

function HealthFacilityModal() {
  const { showFacilityDetail, selectedFacilityId, hideFacilityDetail } =
    useView()
  const { facilities } = useData()
  const { activeCategory } = useFilters()

  // Find the selected facility from all facilities, not just filtered ones
  const allFacilities = facilities[activeCategory] || []
  const facility = allFacilities.find(f => f.id === selectedFacilityId)

  // Close modal on escape key and manage body overflow
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape') {
        hideFacilityDetail()
      }
    }

    if (showFacilityDetail) {
      // Store original overflow value
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleEscape)

      return () => {
        document.removeEventListener('keydown', handleEscape)
        // Restore original overflow value
        document.body.style.overflow = originalOverflow
      }
    } else {
      // Ensure overflow is reset when modal is not showing
      document.body.style.overflow = ''
    }
  }, [showFacilityDetail, hideFacilityDetail])

  // Safety cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  if (!showFacilityDetail || !facility) {
    return null
  }

  // Get available services and infrastructure consistently
  const services = getAvailableServices(facility)
  const infrastructure = getAvailableInfrastructure(facility)

  const supplies = [
    {
      id: 'digital_respiratory_timers',
      name: 'Digital Respiratory Timers',
      icon: '⏱️',
    },
    { id: 'oral_rehydration_salts', name: 'ORS Sachets', icon: '💊' },
    { id: 'zinc_tablets', name: 'Zinc Tablets', icon: '💊' },
    {
      id: 'rapid_diagnostic_test_kits_for_malaria',
      name: 'Malaria RDT Kits',
      icon: '🔬',
    },
    {
      id: 'act_tablets_for_treatment_of_malaria',
      name: 'ACT Tablets',
      icon: '💊',
    },
    {
      id: 'amoxicillin_tablets_for_treatment_of_pneumonia',
      name: 'Amoxicillin',
      icon: '💊',
    },
    {
      id: 'digital_clinical_thermometers_for_measuring_fever',
      name: 'Digital Thermometers',
      icon: '🌡️',
    },
  ].filter(supply => facility[supply.id] === 'Yes')

  const sanitation = [
    { id: 'toilet_for_females', name: 'Female Toilets', icon: '🚺' },
    {
      id: 'toilet_for_people_with_reduced_mobility',
      name: 'Accessible Toilets',
      icon: '♿',
    },
    { id: 'toilet_for_staff', name: 'Staff Toilets', icon: '🚹' },
    {
      id: 'hand_washing_facilities',
      name: 'Hand Washing Facilities',
      icon: '🧼',
    },
  ].filter(item => facility[item.id] === 'Yes')

  const getLevelColor = level => {
    switch (level) {
      case 'HCII':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'HCIII':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'HCIV':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'Hospital':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'Clinic':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Calculate total staff
  const staffFields = [
    'laboratory_assistants',
    'laboratory_technicians',
    'medical_officers',
    'nursing_assistants',
    'clinical_officers',
    'enrolled_midwives',
    'nursing_officers',
    'vhts_chws',
    'comprehensive_enrolled_nurse',
    'enrolled_nurses',
    'health_information_assistants',
  ]
  const totalStaff = staffFields.reduce((sum, field) => {
    const value = parseInt(facility[field]) || 0
    return sum + value
  }, 0)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">
              {facility.name}
            </h2>
            <div className="flex items-center space-x-3 mt-2">
              <span
                className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getLevelColor(facility.facility_level)}`}
              >
                {facility.level}
              </span>
              <span className="text-sm text-gray-500">
                {facility.ownership}
              </span>
            </div>
          </div>
          <button
            onClick={hideFacilityDetail}
            className="text-gray-400 hover:text-gray-600 transition-colors text-2xl font-light ml-4"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Basic Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Location */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  📍 Location
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">District:</span>{' '}
                    {facility.district}
                  </div>
                  <div>
                    <span className="font-medium">Subcounty:</span>{' '}
                    {facility.subcounty}
                  </div>
                  <div>
                    <span className="font-medium">Parish:</span>{' '}
                    {facility.parish}
                  </div>
                  <div>
                    <span className="font-medium">Village:</span>{' '}
                    {facility.village}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              {(facility.officer_in_charge ||
                facility.phone ||
                facility.email) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    📞 Contact
                  </h3>
                  <div className="space-y-2 text-sm">
                    {facility.officer_in_charge && (
                      <div>
                        <span className="font-medium">Officer in Charge:</span>{' '}
                        {facility.officer_in_charge}
                      </div>
                    )}
                    {facility.phone && (
                      <div>
                        <span className="font-medium">Phone:</span>{' '}
                        {facility.phone}
                      </div>
                    )}
                    {facility.email && (
                      <div>
                        <span className="font-medium">Email:</span>{' '}
                        {facility.email}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Staff Count */}
              {totalStaff > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    👥 Staffing
                  </h3>
                  <div className="text-2xl font-semibold text-primary-600">
                    {totalStaff}
                  </div>
                  <div className="text-sm text-gray-500">
                    Total Staff Members
                  </div>
                </div>
              )}
            </div>

            {/* Right Columns - Services & Infrastructure */}
            <div className="lg:col-span-2 space-y-6">
              {/* Services */}
              {services.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    ⭐ Services Offered
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {services.map(service => (
                      <div
                        key={service.id}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <span className="text-green-500">✅</span>
                        <span className="mr-1">{service.icon}</span>
                        <span>{service.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Infrastructure */}
              {infrastructure.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    🏢 Infrastructure
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {infrastructure.map(item => (
                      <div
                        key={item.id}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <span className="text-green-500">✅</span>
                        <span className="mr-1">{item.icon}</span>
                        <span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Supplies & Equipment */}
              {supplies.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    💊 Supplies & Equipment
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {supplies.map(supply => (
                      <div
                        key={supply.id}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <span className="text-green-500">✅</span>
                        <span className="mr-1">{supply.icon}</span>
                        <span>{supply.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Water & Sanitation */}
              {sanitation.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    🚿 Water & Sanitation
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sanitation.map(item => (
                      <div
                        key={item.id}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <span className="text-green-500">✅</span>
                        <span className="mr-1">{item.icon}</span>
                        <span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="mt-8 pt-6 border-t border-surface-200 flex justify-end">
            <button onClick={hideFacilityDetail} className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HealthFacilityModal
